// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import { useQuery } from '@apollo/client';
import {
  ORDER_STATUS,
  type WeightUnitPlusTrays,
  displayCurrency,
  formatTime,
  getCurrencySymbol,
  utils,
} from '@seedplanner/shared-utils';
import Decimal from 'decimal.js';
import gql from 'graphql-tag';
// biome-ignore lint/style/useImportType: <explanation>
import * as React from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams } from 'react-router';
import { ActionButtonBase } from '../components/common/button';
import { TitleCard } from '../components/common/card';
import { PageError } from '../components/common/error';
import { Time } from '../components/common/time';
import PageView from '../components/common/view/PageView';
import HarvestTable from '../components/harvest/HarvestTable';
import { groupPlantingsByTime } from '../components/harvest/groupPlantingsByTime';
import OrderDeficitsTable from '../components/order/OrderDeficitsTable';
import OrderGanttChart from '../components/order/OrderGanttChart';
import OrderInputsTable from '../components/order/OrderInputsTable';
import OrderOptions from '../components/order/OrderOptions';
import { sortOrderInputs } from '../components/order/sortOrderInputs';
import { PlantingTable } from '../components/planting';
import { RecurringOrderIdLink } from '../components/recurringOrder';
import { TaskTable } from '../components/task';
import { useUserContext } from '../context/UserContext';
import { Box } from '../lib/base';
import * as routes from '../routes';
import type { SingleOrderQuery, SingleOrderQueryVariables } from '../types/__generated__/graphql';
import {
  addQuotes,
  calculateAmountsAvailableFromPlantings,
  calculateAmountsNeededFromInputsAndExistingPlantings,
  createCSVContent,
  createID,
  downloadCSV,
  formatOrderInput,
  formatRecurringOrderName,
  formatStateOrStatus,
  getDeficitsFromAmountsNeededAndAvailable,
  getNumericID,
  orderInputsToCalcInputs,
  plantingInputsToCalcInputs,
} from '../utils';

const SINGLE_ORDER_QUERY = gql`
  query SingleOrder($id: ID!) {
    order(id: $id) {
      id
      order_status
      customer
      notes
      order_fulfillment_time
      recurring_order_id
      created_at
      updated_at
      recurring_order {
        id
      }
      inputs {
        edges {
          node {
            id
            size_amount
            quantity
            unit
            price_per_item
            crop {
              id
              crop_name
              yield_per_tray
              yield_unit
              total_grow_time
              harvest_buffer_time
            }
            blend {
              id
              blend_name
              status
              crops {
                edges {
                  node {
                    id
                    percent_of_blend
                    crop {
                      id
                      crop_name
                      yield_per_tray
                      yield_unit
                      total_grow_time
                      harvest_buffer_time
                    }
                  }
                }
              }
            }
          }
        }
      }
      tasks {
        edges {
          node {
            id
            task_name
            task_status
            task_time
            planting {
              id
              order_id
              trays_rounded
            }
            crop {
              id
              crop_name
            }
          }
        }
      }
      plantings {
        edges {
          node {
            id
            planting_time
            planting_status
            planting_state
            trays_rounded
            yield_per_tray_ounces
            crop {
              id
              crop_name
              total_grow_time
            }
            order_id
            tasks {
              count
            }
          }
        }
      }
    }
  }
`;

export default function OrderView() {
  const { orderId } = useParams<{ readonly orderId: string }>();
  const { permissions, timezone, currencyCode } = useUserContext();
  const currencySymbol = getCurrencySymbol(currencyCode);
  if (orderId === undefined) {
    return <PageError />;
  }
  const { data, refetch, loading, error } = useQuery<SingleOrderQuery, SingleOrderQueryVariables>(SINGLE_ORDER_QUERY, {
    variables: { id: createID.order(orderId) },
  });

  if (error) {
    return <PageError error={error} />;
  }
  if (loading || data?.order == null) {
    return (
      <PageView
        title="Order"
        name="Order"
        pluralName="Orders"
        pluralRoute={routes.ORDERS}
        loading={loading}
        searchRoute={''}
        bodyColumns={[]}
      />
    );
  }

  const userCanEditOrders = permissions.can_edit_orders;

  const inputs = data.order.inputs.edges.map(({ node }) => node);
  const totalRevenue = inputs.reduce((acc, input) => {
    if (input.price_per_item == null) {
      return acc;
    }
    const price = new Decimal(input.price_per_item);
    const quantity = new Decimal(input.quantity);

    return acc.add(price.mul(quantity));
  }, new Decimal(0));
  const totalRevenueFormatted = totalRevenue.isZero()
    ? `${currencySymbol} -`
    : displayCurrency({
        amount: totalRevenue.toNumber(),
        currencyCode,
      });

  const plantings = data.order.plantings.edges.map(({ node }) => node);
  const order = data.order;

  let deficitsView = null;
  let buffersView = null;
  if (order.order_status !== ORDER_STATUS.CANCELLED) {
    const amountsNeeded = calculateAmountsNeededFromInputsAndExistingPlantings(
      orderInputsToCalcInputs(inputs),
      plantings,
    );
    const amountsAvailable = calculateAmountsAvailableFromPlantings(plantingInputsToCalcInputs(plantings));
    const deficits = getDeficitsFromAmountsNeededAndAvailable(amountsNeeded, amountsAvailable);
    const deficitsInput = [...deficits.values()].filter((d) => d.traysDeficit > 0);
    const surplusInputs = [...deficits.values()].filter((d) => d.traysDeficit < 0);

    deficitsView =
      deficitsInput.length > 0 ? (
        <Box sx={{ mt: 2 }}>
          <TitleCard
            title="Deficits"
            rightElement={<div />}
            loading={loading}
            content={<OrderDeficitsTable deficits={deficitsInput} />}
          />
        </Box>
      ) : null;

    buffersView =
      surplusInputs.length > 0 ? (
        <Box sx={{ mt: 2 }}>
          <TitleCard
            title="Safety Margins"
            rightElement={<div />}
            loading={loading}
            content={<OrderDeficitsTable useForBuffers deficits={surplusInputs} />}
          />
        </Box>
      ) : null;
  }

  const inputsSortedByCropBlendName = sortOrderInputs(inputs);
  const exportPackListToCsvButton = (
    <ActionButtonBase
      title="Export to CSV"
      showButton={true}
      disabled={loading}
      onClick={() => {
        const headers = ['Customer', 'Crop/Blend', 'Quantity', 'Unit Price', 'Amount'];
        let totalAmount = new Decimal(0);
        const formattedInputs = inputs.map((input) => {
          const cropName = input.crop?.crop_name ?? input.blend?.blend_name ?? 'Unknown';
          const inputStr = formatOrderInput({
            quantity: input.quantity,
            sizeAmount: input.size_amount,
            sizeUnit: input.unit as WeightUnitPlusTrays,
          });
          const pricePerItem =
            input.price_per_item != null
              ? `${currencySymbol}${Number.parseFloat(input.price_per_item).toFixed(2)}`
              : '-';
          let totalPrice = '-';
          if (input.price_per_item != null) {
            const amount = new Decimal(input.price_per_item).mul(input.quantity);
            totalPrice = `${currencySymbol}${amount.toFixed(2, Decimal.ROUND_HALF_UP)}`;
            totalAmount = totalAmount.add(amount);
          }
          return [order.customer || 'Unknown', cropName, inputStr, pricePerItem, totalPrice].map(addQuotes);
        });
        // Add final row with total amount
        const totalRow = ['', '', '', 'Total', `${currencySymbol}${totalAmount.toFixed(2, Decimal.ROUND_HALF_UP)}`].map(
          addQuotes,
        );
        downloadCSV({
          title: `${order.customer || 'Unknown'} - ${formatTime(order.order_fulfillment_time, { timezone, year: false, time: false })} Pack List`,
          content: createCSVContent({
            data: [headers, ...formattedInputs, totalRow],
          }),
        });
      }}
    />
  );
  const inputsView = (
    <Box sx={{ mt: 2 }}>
      <TitleCard
        title="Pack List"
        rightElement={exportPackListToCsvButton}
        loading={loading}
        content={<OrderInputsTable orderInputs={inputsSortedByCropBlendName} />}
      />
    </Box>
  );

  const groupedPlantings = Array.from(groupPlantingsByTime(plantings).values());
  groupedPlantings.sort((a, b) => a.time - b.time);
  const groupedPlantingsView = (
    <Box sx={{ mt: 2 }}>
      <TitleCard
        title="Associated Harvests"
        rightElement={<div />}
        loading={loading}
        content={<HarvestTable showOrdersAndStatus={false} harvestGroups={groupedPlantings.map((g) => g.plantings)} />}
      />
    </Box>
  );

  const plantingsView = (
    <Box sx={{ mt: 2 }}>
      <TitleCard
        title="Plantings"
        rightElement={<div />}
        loading={loading}
        content={<PlantingTable showOrder={false} plantings={plantings} />}
      />
    </Box>
  );
  const tasks = order.tasks.edges.map(({ node }) => node);
  const tasksView = (
    <Box sx={{ mt: 2 }}>
      <TitleCard
        title="Tasks"
        rightElement={<div />}
        loading={loading}
        content={<TaskTable tasks={tasks} showOrder={false} />}
      />
    </Box>
  );

  const ganttChartView = (
    <Box sx={{ mt: 2 }}>
      <TitleCard
        title="Grow Timeline"
        rightElement={<div />}
        loading={loading}
        content={<OrderGanttChart orderTime={order.order_fulfillment_time} plantings={plantings} />}
      />
    </Box>
  );

  const extraView = (
    <>
      {deficitsView}
      {inputsView}
      {ganttChartView}
      {groupedPlantingsView}
      {buffersView}
      {plantingsView}
      {tasksView}
    </>
  );

  const maybeRecurringOrderId = order.recurring_order_id ?? undefined;
  let recurringOrder: React.ReactElement | string = '-';
  if (maybeRecurringOrderId) {
    const recurringOrderExists = order.recurring_order !== null;
    recurringOrder = recurringOrderExists ? (
      <RecurringOrderIdLink id={maybeRecurringOrderId} />
    ) : (
      `${formatRecurringOrderName(maybeRecurringOrderId)} (deleted)`
    );
  }

  const columns: Array<
    [string, React.ReactElement | string | number] | [string, React.ReactElement | string | number, number]
  > = [
    ['Order ID', `${getNumericID(order.id)}`],
    ['Recurring Order', recurringOrder],
    ['Customer', order.customer || '-'],
    ['Total Revenue', totalRevenueFormatted],
    ['Delivery Date', <Time key={order.order_fulfillment_time} time={order.order_fulfillment_time} />],
    ['Status', formatStateOrStatus(order.order_status)],
    [
      'Total Trays',
      utils.round(
        plantings.reduce((acc, planting) => utils.addTraysRaw(acc, Number.parseFloat(planting.trays_rounded)), 0),
      ),
    ],
    ['Notes', order.notes || '-'],
    [
      'Updated At',
      <Time
        key={order.order_fulfillment_time}
        time={order.updated_at}
        options={{ time: true, removeDayOfWeek: true }}
        dontUseTimezone
      />,
    ],
    [
      'Created At',
      <Time
        key={order.order_fulfillment_time}
        time={order.created_at}
        options={{ time: true, removeDayOfWeek: true }}
        dontUseTimezone
      />,
    ],
  ];

  const headerRightElement = userCanEditOrders ? (
    <OrderOptions
      orderId={Number.parseInt(orderId)}
      orderFulfillmentTime={order.order_fulfillment_time}
      orderStatus={order.order_status}
      onSuccess={(shouldRefetchSelf) => {
        if (shouldRefetchSelf) {
          void refetch();
        }
      }}
    />
  ) : (
    <></>
  );

  return (
    <>
      <Helmet>
        <title>{`${order.customer} - Order ${orderId}`}</title>
      </Helmet>
      <PageView
        title="Order"
        name="Order"
        pluralName="Orders"
        pluralRoute={routes.ORDERS}
        searchRoute={routes.makeOrderSearch(getNumericID(order.id))}
        bodyColumns={columns}
        extra={extraView}
        loading={loading}
        headerRightElement={headerRightElement}
      />
    </>
  );
}
